import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import CommunityPrefs from "@/components/community/CommunityPrefs";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatDateRange } from "@/lib/format";
import { Flame, Crown, Heart, PlusCircle, Shield, ShoppingBag } from "lucide-react";

export default function Profile() {
  const { lang, t, tCategory } = useLang();
  const { user, loading } = useAuth();

  const { data: favorites = [] } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data, enabled: !!user });
  const { data: myEvents = [] } = useQuery({ queryKey: ["myEvents"], queryFn: async () => (await api.get("/my/events")).data, enabled: !!user });
  const { data: orders = [] } = useQuery({ queryKey: ["myOrders"], queryFn: async () => (await api.get("/store/orders")).data, enabled: !!user });
  const { data: feed = [] } = useQuery({ queryKey: ["feed"], queryFn: async () => (await api.get("/feed")).data, enabled: !!user });

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/profile")}`;
  };

  if (loading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <Flame className="w-12 h-12 text-amber-500 mx-auto mb-6" />
        <h1 className="font-serif text-2xl text-amber-100 mb-4">{t("nav_profile")}</h1>
        <p className="text-stone-400 mb-8">{t("login_desc")}</p>
        <button data-testid="profile-login-btn" onClick={handleLogin} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{t("login_google")}</button>
      </div>
    );
  }

  const badge = user.subscription === "organizer" ? "Organisateur Pro" : user.subscription === "member" ? "Cercle Premium" : null;

  return (
    <>
      <Seo title={t("nav_profile")} canonicalPath="/profile" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-12 pb-8 border-b border-amber-900/30" data-testid="profile-header">
          {user.picture ? <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-amber-700/50" /> : <span className="w-20 h-20 rounded-full bg-amber-900/40 flex items-center justify-center"><Flame className="w-8 h-8 text-amber-400" /></span>}
          <div className="flex-1">
            <h1 className="font-serif text-3xl text-amber-100">{user.name}</h1>
            <p className="text-stone-400 text-sm">{user.email}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {user.role === "admin" && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase bg-red-950/50 text-red-300 border border-red-800/40"><Shield className="w-3 h-3" /> Admin</span>}
              {badge ? <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase bg-amber-950/60 text-amber-300 border border-amber-800/40"><Crown className="w-3 h-3" /> {badge}</span>
                : <Link to="/membership" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase bg-[#181A20] text-stone-400 border border-[#272A34] hover:border-amber-500/40"><Crown className="w-3 h-3" /> {t("nav_membership")}</Link>}
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/submit" data-testid="profile-submit-link" className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2.5 rounded-md text-sm"><PlusCircle className="w-4 h-4" /> {t("nav_submit")}</Link>
          </div>
        </div>

        <CommunityPrefs />

        <section className="mb-14">
          <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><Heart className="w-5 h-5 text-amber-500" /> {t("fav_title")} ({favorites.length})</h2>
          {favorites.length === 0 ? <p className="text-stone-400">{t("fav_empty")}</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{favorites.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
          )}
        </section>

        {feed.length > 0 && (
          <section className="mb-14" data-testid="follow-feed">
            <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> {t("feed_title")} ({feed.length})</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{feed.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
          </section>
        )}

        <section>
          <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><PlusCircle className="w-5 h-5 text-amber-500" /> {t("my_events")} ({myEvents.length})</h2>
          {myEvents.length === 0 ? <p className="text-stone-400">—</p> : (
            <div className="space-y-3" data-testid="my-events-list">
              {myEvents.map((e) => (
                <div key={e.id} className="flex items-center gap-4 bg-[#121418] border border-[#272A34] rounded-lg p-4">
                  {e.image && <img src={e.image} alt="" className="w-16 h-16 rounded-md object-cover" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-stone-100 truncate">{e.title}</h3>
                    <div className="text-xs text-stone-500 font-mono">{tCategory(e.category)} · {formatDateRange(e.start_date, e.end_date, lang)}</div>
                    {e.promoted && (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] font-mono" data-testid={`promo-stats-${e.slug}`}>
                        <span className="text-amber-400">{t("promo_views")}: {e.promo_views || 0}</span>
                        <span className="text-amber-400">{t("promo_clicks")}: {e.promo_clicks || 0}</span>
                        <span className="text-stone-500">{t("promo_ctr")}: {e.promo_views ? Math.round((e.promo_clicks || 0) / e.promo_views * 100) : 0}%</span>
                        {e.promoted_until && new Date(e.promoted_until) < new Date() && (
                          <span data-testid={`promo-expired-${e.slug}`} className="px-2 py-0.5 rounded-full uppercase tracking-wider bg-red-950/50 text-red-300 border border-red-800/40">{t("promo_expired")}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase shrink-0 ${e.status === "approved" ? "bg-emerald-950/50 text-emerald-300 border border-emerald-800/40" : e.status === "pending" ? "bg-amber-950/50 text-amber-300 border border-amber-800/40" : "bg-red-950/50 text-red-300 border border-red-800/40"}`}>{e.status}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {orders.length > 0 && (
          <section className="mt-14">
            <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-amber-500" /> {t("my_orders")} ({orders.length})</h2>
            <div className="space-y-3" data-testid="my-orders-list">
              {orders.map((o) => (
                <div key={o.order_id} className="flex items-center gap-4 bg-[#121418] border border-[#272A34] rounded-lg p-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-stone-500">{o.order_id}</div>
                    <div className="text-stone-200 text-sm truncate">{o.items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}</div>
                    <div className="font-serif text-amber-400 mt-1">CHF {(o.total_cents / 100).toFixed(2)}</div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase shrink-0 ${o.status === "paid" ? "bg-emerald-950/50 text-emerald-300 border border-emerald-800/40" : "bg-amber-950/50 text-amber-300 border border-amber-800/40"}`}>{o.status === "paid" ? t("order_paid") : t("order_pending")}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
